import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Row, Col, Table } from "react-bootstrap";
import { useToasts } from "react-toast-notifications";

import Head from "@/components/Content/Head";
import classes from "../maintainers.module.scss";

import api from "@/services/api.service";
import { hasPermission, toast } from "@/services/Utils";

import BasePagination from "@/components/Pagination/BasePagination";
import Fetching from "@/components/Fetching/Fetching";
import BasicConfirm from "@/components/Confirm/BasicConfirm";
import ButtonFilter from "@/components/Filter/ButtonFilter";
import ApplicationsTableHead from "./ApplicationsTableHead";
import ApplicationsTableBody from "./ApplicationsTableBody";
import ApplicationsForm from "./ApplicationsForm/ApplicationsForm";
import ApplicationsFilter from "./ApplicationsFilter";
import { filters } from "./applications.constants";

const Applications = () => {
	const { addToast } = useToasts();

	const { search, estate_id } = useSelector((state) => state.auth.filter);

	const [isFetching, setIsFetching] = useState(true);
	const [selected, setSelected] = useState("");
	const [paginate, setPaginate] = useState(20);
	const [page, setPage] = useState(1);
	const [lastPage, setLastPage] = useState("");
	const [quantity, setQuantity] = useState(0);
	const [from, setFrom] = useState(0);
	const [to, setTo] = useState(0);
	const [data, setData] = useState([]);

	const [customers, setCustomers] = useState([]);
	const [filter, setFilter] = useState({ ...filters });
	const [permissions, setPermissions] = useState([]);

	const [showFilter, setShowFilter] = useState(false);
	const [showDelete, setShowDelete] = useState(false);

	useEffect(() => {
		setIsFetching(true);
		api.get("/admin/applications", {
			search,
			estate_id,
			page,
			paginate,
			...filter,
		}).then((response) => {
			updateData(response);
		});
	}, [search, estate_id, paginate, page, filter]);

	const updateData = (response) => {
		const { list, customers, permissions: allPermissions } = response;
		setData(list.data);
		setQuantity(list.total);
		setTo(list.to);
		setFrom(list.from);
		setLastPage(list.last_page);
		setCustomers(customers);
		setPermissions(allPermissions);
		setIsFetching(false);
	};

	const handleDelete = (item) => {
		setSelected(item.applications_history_id);
		setShowDelete(true);
	};

	const confirmDelete = () => {
		setShowDelete(false);
		setPage(1);

		setIsFetching(true);
		api.post("/admin/applications/delete", {
			page,
			selected,
			paginate,
			search,
			estate_id,
			...filter,
		}).then((response) => {
			if (Object.prototype.hasOwnProperty.call(response, "list")) {
				toast(addToast, "Aplicación eliminada correctamente", "success");
				updateData(response);
			} else {
				setIsFetching(false);
			}
		});
	};

	const confirmSave = (form, modalFetching, afterSave) => {
		modalFetching(true);
		setPage(1);

		api.post("/admin/applications/save", {
			...form,
			page,
			paginate,
			search,
			estate_id,
			...filter,
		}).then((response) => {
			if (Object.prototype.hasOwnProperty.call(response, "list")) {
				updateData(response);
				modalFetching(false);
				afterSave(true);
			} else {
				modalFetching(false);
			}
		});
	};

	const applyFilter = (params) => {
		let new_filter = { ...filter };

		if (params.filterCustomer !== filter.filter_customer_id) {
			new_filter = {
				...new_filter,
				filter_customer_id: params.filterCustomer,
			};
		}
		if (params.filterDate !== filter.filter_date) {
			new_filter = {
				...new_filter,
				filter_date: params.filterDate,
			};
		}
		if (params.filterDateTo !== filter.filter_date_to) {
			new_filter = {
				...new_filter,
				filter_date_to: params.filterDateTo,
			};
		}
		setFilter(new_filter);
		setShowFilter(false);
	};

	if (isFetching) {
		return <Fetching />;
	}

	return (
		<>
			<Head title="Aplicaciones" button={false} back_url={"/mantenedores"} />

			<ButtonFilter origin="main" size_md={12} showFilter={showFilter} setShowFilter={setShowFilter} />

			{hasPermission("show", permissions) && (
				<Row className={` ${classes.main_row}`}>
					{showFilter && (
						<>
							<ApplicationsFilter filter={filter} applyFilter={applyFilter} customers={customers} />
						</>
					)}

					<Col md={showFilter ? 9 : 12} className={classes.main_col}>
						<Row className={classes.header}>
							<ButtonFilter origin="actions" size_md={1} showFilter={showFilter} setShowFilter={setShowFilter} />
						</Row>

						<Row className={classes.table}>
							<div className={classes.table_div}>
								<Table className={classes.table_desktop}>
									<ApplicationsTableHead />
									<ApplicationsTableBody data={data} handleDelete={handleDelete} permissions={permissions} />
								</Table>
							</div>
						</Row>

						<BasePagination
							page={page}
							paginate={paginate}
							last_page={lastPage}
							quantity={quantity}
							from={from}
							to={to}
							setPage={setPage}
							setPaginate={setPaginate}
						/>
					</Col>
				</Row>
			)}

			<BasicConfirm showConfirm={showDelete} setShowConfirm={setShowDelete} handleAction={confirmDelete} />
			<ApplicationsForm confirmSave={confirmSave} permissions={permissions} />
		</>
	);
};

export default Applications;
